/**
 * Number charts: the multiplication table (and, later, the hundreds chart).
 */

import { type Rng, shuffled } from "@/lib/tool-kit/worksheets/random";

export const MULTIPLICATION_SIZES = [10, 12, 15, 20] as const;
export type MultiplicationSize = (typeof MULTIPLICATION_SIZES)[number];

/** Type size in points for the cells of each chart size, so a row still fits the page. */
export const MULTIPLICATION_FONT_POINTS: Record<MultiplicationSize, number> = {
  10: 15,
  12: 13,
  15: 11,
  20: 9,
};

/** The factors along each edge: 1 to `size`, or 0 to `size`. */
export function chartFactors(size: number, includeZero: boolean): number[] {
  const first = includeZero ? 0 : 1;
  return Array.from({ length: size - first + 1 }, (_, i) => first + i);
}

export function cellKey(row: number, column: number): string {
  return `${row}×${column}`;
}

/**
 * Cells whose product is left empty. `fraction` of all cells (rounded) are chosen at
 * random without repeats; 0 leaves none and 1 blanks every cell.
 */
export function blankCells(
  factors: readonly number[],
  fraction: number,
  rng: Rng
): Set<string> {
  const keys = factors.flatMap((row) =>
    factors.map((column) => cellKey(row, column))
  );
  const wanted = Math.round(Math.min(1, Math.max(0, fraction)) * keys.length);
  if (wanted >= keys.length) {
    return new Set(keys);
  }
  return new Set(shuffled(rng, keys).slice(0, wanted));
}

/** Whether a cell sits in the highlighted number's row or column. */
export function inFocus(
  row: number,
  column: number,
  focus: number | null
): boolean {
  return focus !== null && (row === focus || column === focus);
}

/** Squares run down the diagonal: 1 × 1, 2 × 2, 3 × 3 … */
export function isSquareCell(row: number, column: number): boolean {
  return row === column;
}

/** How many different products appear in the 1 to `size` table. */
export function distinctProducts(size: number): number {
  const products = new Set<number>();
  for (let a = 1; a <= size; a += 1) {
    for (let b = a; b <= size; b += 1) {
      products.add(a * b);
    }
  }
  return products.size;
}

/* --------------------------------------------------------------- hundreds chart */

export type HundredsRange = "1-100" | "1-120" | "0-99";
export type SkipMode = "multiples" | "from-start";

export const HUNDREDS_COLUMNS = 10;

/** The numbers on the chart, in reading order; always ten to a row. */
export function hundredsNumbers(range: HundredsRange): number[] {
  const [first, last] = range.split("-").map(Number) as [number, number];
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}

/**
 * Numbers to shade when counting by `step`. `multiples` shades every number divisible by
 * the step (0 counts as a multiple); `from-start` counts on from the chart's first
 * number: 1, 4, 7 … when counting by 3 from 1. A step below 2 shades nothing.
 */
export function skipShaded(
  numbers: readonly number[],
  step: number,
  mode: SkipMode
): Set<number> {
  if (step < 2 || numbers.length === 0) {
    return new Set();
  }
  const first = numbers[0] as number;
  return new Set(
    numbers.filter((n) =>
      mode === "multiples" ? n % step === 0 : (n - first) % step === 0
    )
  );
}

/** A random `fraction` of the numbers to leave blank (1 blanks all of them). */
export function blankNumbers(
  numbers: readonly number[],
  fraction: number,
  rng: Rng
): Set<number> {
  const wanted = Math.round(
    Math.min(1, Math.max(0, fraction)) * numbers.length
  );
  if (wanted >= numbers.length) {
    return new Set(numbers);
  }
  return new Set(shuffled(rng, numbers).slice(0, wanted));
}

/** Splits the numbers into rows of ten. */
export function hundredsRows(numbers: readonly number[]): number[][] {
  const rows: number[][] = [];
  for (let i = 0; i < numbers.length; i += HUNDREDS_COLUMNS) {
    rows.push(numbers.slice(i, i + HUNDREDS_COLUMNS));
  }
  return rows;
}
